/**
 * **Removed on this device** — the servers a person took out of the Local page's
 * table with `Forget` (plan `docs/plans/LOCAL-SERVER-ONBOARDING-JOURNEY.md` §3/C4,
 * owner decision §7 Q3 — card `t_36033b60`).
 *
 * The owner's report: *"can not delet saved entry.. if i click forget it stay in
 * the list.."*. The word he chose afterwards is **remove**: the row leaves the
 * table for every row source, restart included. The row comes from one of two
 * places and this module is the half the table cannot be without:
 *
 *   - a row this **device** saved (a connection it reached by address) is deleted
 *     outright — `services/savedConnections.ts`, the path `Forget` always had;
 *   - a row the **account** lists is the cloud's, and this device may not delete
 *     from it. So the removal is kept *here*, as a small persisted set, and
 *     `services/localServerTable.ts` → `buildLocalServerRows` filters it — the one
 *     table, so every shape and every consumer agrees (a phone card and a desktop
 *     row are the same rows).
 *
 * ── What this is not ────────────────────────────────────────────────────────
 *
 * It is not a cloud delete: the account centre still lists the server, and that
 * is deliberate (§7 Q3 — the owner keeps his list, this device keeps its own
 * view). It is not a permanent block either: anything that re-establishes the
 * server on this device — `+ Dodaj strežnik` with its address, opening it from
 * **Found on this network**, adopting it, running its onboarding — calls
 * `restoreForgottenServer`, and the row is back in the table.
 *
 * ── The key ────────────────────────────────────────────────────────────────
 *
 * An entry is keyed by the **cloud row's id**, falling back to the **hub's own
 * id** when the row the person removed had no account row of its own (a hub this
 * device reached by address). A row is hidden when it names **any** id the entry
 * holds, so a removal taken on the account's row also covers the same server
 * approached by its own id — and two account rows that share a *label* but not an
 * id (the two `Dev` rows on the owner's box) stay apart, which is what "remove
 * *this* row" means. `cloudId` and `hubId` are recorded beside the key so that
 * the **way back** can clear the entry by whichever id that path knows.
 *
 * ── Storage ────────────────────────────────────────────────────────────────
 *
 * Key `otto_forgotten_servers`, in the same family as the other buffers
 * (`otto_server_profiles`, `otto_saved_connections`; `torik_device_token:*` and
 * `hubAccessToken:*` are untouched). The payload is versioned:
 *
 *     { version: 1, savedAt, rows: [{ key, cloudId, hubId, name, at }, …] }
 *
 * The reader is as tolerant as `services/serverProfiles.ts`'s: a corrupt value, a
 * payload from an older schema, a payload from a newer one, a row missing what
 * this build reads — none of it may throw (the table is painted at launch, before
 * any network call), an unreadable row is dropped, and the readable ones are
 * kept.
 */
import { ref } from 'vue';
import { activeProfileId, onProfileScopeChange, profileScopedKey } from './profileScope';

/** The storage key this module owns. */
export const FORGOTTEN_SERVERS_STORAGE_KEY = 'otto_forgotten_servers';

/** The schema this build writes. */
export const FORGOTTEN_SERVERS_SCHEMA_VERSION = 1;

/** Hard cap on held entries — a device cannot remove servers forever. */
export const FORGOTTEN_SERVERS_LIMIT = 50;

/** The slice of `Storage` this module needs (injected by tests). */
export interface ForgottenServerStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

/** One server, as removed on this device. */
export interface ForgottenServer {
  /**
   * What the removal is held under: the cloud row's id, else the hub's own id.
   * Both are lowercased the way the buffers key them.
   */
  key: string;
  /** The account's row id for the server, when the removed row had one. */
  cloudId: string | null;
  /** The hub's own id, when it was known — the way a re-add finds this entry. */
  hubId: string | null;
  /** The label the row painted when it was removed, so a surface can name it. */
  name: string | null;
  /** When it was removed (ISO). */
  at: string;
}

/** The persisted payload. */
export interface ForgottenServerStore {
  version: number;
  /** When the set was last written (ISO), or null. */
  savedAt: string | null;
  rows: ForgottenServer[];
}

/** What a caller may name a server by. Both spellings of the id, or one. */
export interface ForgottenServerRef {
  cloudId?: string | null;
  hubId?: string | null;
}

/**
 * The set, as this session reads it. Reactive: the table is a `computed` over it,
 * so a removal repaints the page on the next tick and nothing has to be re-read
 * by hand.
 */
export const forgottenServers = ref<ForgottenServer[]>([]);

// ─────────────────────────────────────────────────────────────────────────────
// Field guards — every field of every row, read through its own
// ─────────────────────────────────────────────────────────────────────────────

function idText(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim().toLowerCase();
  return trimmed.length ? trimmed : null;
}

function text(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  return trimmed.length ? trimmed : null;
}

/** An ISO string a reader can trust, or null. */
function isoText(value: unknown): string | null {
  const raw = text(value);
  if (!raw) return null;
  return Number.isNaN(new Date(raw).getTime()) ? null : raw;
}

/** One stored row, read field by field — an unreadable row answers null. */
export function readForgottenServer(value: unknown): ForgottenServer | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
  const raw = value as Record<string, unknown>;
  const cloudId = idText(raw.cloudId);
  const hubId = idText(raw.hubId);
  // The key is the cloud row's id, else the hub's own — a record that carries
  // neither names no server this set can hold, and keeping it would only be a
  // row the next `persist` has to carry for nothing.
  const key = idText(raw.key) ?? cloudId ?? hubId;
  if (!key) return null;
  return {
    key,
    // The key's own value is one of the two: a payload written before the fields
    // were spelled apart still reads as the server it names.
    cloudId: cloudId ?? (hubId ? null : key),
    hubId: hubId ?? (cloudId ? null : key),
    name: text(raw.name),
    at: isoText(raw.at) ?? new Date(0).toISOString(),
  };
}

/** The rows of a stored payload — the tolerant half of the reader. */
export function readForgottenServerRows(value: unknown): ForgottenServer[] {
  if (!Array.isArray(value)) return [];
  const rows: ForgottenServer[] = [];
  for (const entry of value) {
    const row = readForgottenServer(entry);
    if (!row) continue;
    // Two entries for one server (an older build, a double write): the first one
    // wins, so the set never answers "which of you?".
    if (rows.some((existing) => existing.key === row.key)) continue;
    rows.push(row);
  }
  return rows;
}

/** A stored payload, read leniently: anything unusable degrades to an empty set. */
export function readForgottenServerStore(value: unknown): ForgottenServerStore {
  const payload =
    typeof value === 'string'
      ? (() => {
          try {
            return JSON.parse(value) as unknown;
          } catch {
            return null;
          }
        })()
      : value;
  // A bare array (a payload written by a build that stored the rows alone) is
  // read as the rows it is, rather than as an unusable object.
  if (Array.isArray(payload)) {
    return { version: FORGOTTEN_SERVERS_SCHEMA_VERSION, savedAt: null, rows: readForgottenServerRows(payload) };
  }
  if (!payload || typeof payload !== 'object') {
    return { version: FORGOTTEN_SERVERS_SCHEMA_VERSION, savedAt: null, rows: [] };
  }
  const raw = payload as Record<string, unknown>;
  const version = typeof raw.version === 'number' && Number.isFinite(raw.version) ? raw.version : null;
  return {
    // The version as it was written: a newer build's payload keeps its number
    // until this one writes, and is still read field-wise either way.
    version: version === null ? FORGOTTEN_SERVERS_SCHEMA_VERSION : Math.trunc(version),
    savedAt: isoText(raw.savedAt),
    rows: readForgottenServerRows(raw.rows),
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// Reading and writing the device's storage
// ─────────────────────────────────────────────────────────────────────────────

function defaultStorage(): ForgottenServerStorage | null {
  try {
    return typeof localStorage === 'undefined' ? null : localStorage;
  } catch {
    return null;
  }
}

/**
 * The key this profile's removed-on-this-device set is held under: bare for a
 * device with no account, `<key>:<accountId>` for a signed-in one
 * (`services/profileScope.ts`). The bare key is the **device** bucket — exactly
 * where every set written before this module existed already lives.
 */
function storageKey(): string {
  return profileScopedKey(FORGOTTEN_SERVERS_STORAGE_KEY);
}

/**
 * One hydrate per session: the page reads the set while it renders, the module
 * reads storage once. `resetForgottenServersForTests` clears the guard — and a
 * *restart* is exactly that: in-memory gone, storage untouched.
 */
let hydrated = false;

/**
 * Fill the in-memory set from this device's storage. Synchronous by design — this
 * is the call that lets the table drop a removed server on its first paint.
 *
 * Tolerant in every direction: no storage (a test, SSR), no key (a device that
 * has never removed anything), a corrupt value, a payload from another schema —
 * all answer the rows that could be read, which may be none.
 */
export function hydrateForgottenServers(
  options: { storage?: ForgottenServerStorage | null; force?: boolean } = {},
): ForgottenServer[] {
  if (hydrated && !options.force) return forgottenServers.value;
  const storage = options.storage === undefined ? defaultStorage() : options.storage;
  let store: ForgottenServerStore = { version: FORGOTTEN_SERVERS_SCHEMA_VERSION, savedAt: null, rows: [] };
  if (storage) {
    try {
      store = readForgottenServerStore(storage.getItem(storageKey()));
    } catch {
      // A storage that throws on read (private mode, a broken profile) leaves the
      // set empty — never a crash in the table's first paint.
    }
  }
  forgottenServers.value = store.rows;
  hydrated = true;
  return forgottenServers.value;
}

/** Write the set back to this device's storage. Failures are the storage's, not the caller's. */
function persist(options: { storage?: ForgottenServerStorage | null } = {}): void {
  const storage = options.storage === undefined ? defaultStorage() : options.storage;
  if (!storage) return;
  const payload: ForgottenServerStore = {
    version: FORGOTTEN_SERVERS_SCHEMA_VERSION,
    savedAt: new Date().toISOString(),
    rows: forgottenServers.value,
  };
  try {
    storage.setItem(storageKey(), JSON.stringify(payload));
  } catch (error) {
    // Quota, a locked profile: the in-memory set still answers this session.
    console.warn('[ForgottenServers] could not write the removed-on-this-device set:', error);
  }
}

/** Tests only: forget the in-memory set (storage untouched — a restart). */
export function resetForgottenServersForTests(): void {
  forgottenServers.value = [];
  hydrated = false;
}

/**
 * Take away the **device bucket's** removals — the set a device with no account
 * keeps. Called when the cloud account goes away (`services/signedOutLists.ts`),
 * because a removal made under one account must not hide a row for the next
 * person on this device, and the signed-out device keeps no list at all.
 *
 * An account's bucket is never touched.
 */
export function clearDeviceForgottenServers(options: { storage?: ForgottenServerStorage | null } = {}): void {
  const storage = options.storage === undefined ? defaultStorage() : options.storage;
  try {
    storage?.removeItem(profileScopedKey(FORGOTTEN_SERVERS_STORAGE_KEY, null));
  } catch {
    // ignore — the key is gone or unreadable, which is the state we wanted
  }
  if (activeProfileId() === null) forgottenServers.value = [];
}

// ─────────────────────────────────────────────────────────────────────────────
// The set, as the table and the page read it
// ─────────────────────────────────────────────────────────────────────────────

/** The ids a caller named, lowercased, the way the set keys them. */
function refIds(ref: ForgottenServerRef): string[] {
  return [idText(ref.cloudId), idText(ref.hubId)].filter((id): id is string => !!id);
}

/**
 * The key a removal would be held under: the cloud row's id, else the hub's own.
 * `null` when neither was named — a server this set cannot identify.
 */
export function forgottenServerKey(ref: ForgottenServerRef): string | null {
  return idText(ref.cloudId) ?? idText(ref.hubId);
}

/**
 * The entry that hid this server, or null.
 *
 * Matched by **any** id the entry holds — the key it is held under and the two
 * ids recorded beside it — against any id the caller named. One server is one
 * row (`services/localServerTable.ts` folds a profile and a saved connection of
 * the same hub into one row), and the row that comes back may be spelled with a
 * different id than the row that was removed: a hub first reached by address is
 * keyed by its own id, and the moment the cloud lists it the same profile carries
 * the account's row id too. The removal has to cover both spellings, or a
 * launched row would slip past it — while two account rows that share a *label*
 * but no id (the two `Dev` rows on the owner's box) stay apart, because they name
 * different servers.
 */
export function forgottenServerFor(
  ref: ForgottenServerRef,
  rows: ForgottenServer[] = forgottenServers.value,
): ForgottenServer | null {
  const ids = refIds(ref);
  if (!ids.length) return null;
  return (
    rows.find((entry) => [entry.key, entry.cloudId, entry.hubId].some((id) => !!id && ids.includes(id))) ?? null
  );
}

/**
 * Has this server been removed on this device? The table's own question
 * (`services/localServerTable.ts`), asked with whatever ids a row has: a removal
 * taken on the account's row also covers the same server named by its own id.
 */
export function isServerForgotten(
  ref: ForgottenServerRef,
  rows: ForgottenServer[] = forgottenServers.value,
): boolean {
  return forgottenServerFor(ref, rows) !== null;
}

/**
 * Remove a server on this device: record it, so the account's buffered list
 * cannot paint the row again (this is the half `forget()` in
 * `views/LocalServers.vue` owes an account-listed row — it has no saved
 * connection to delete).
 *
 * Answers the entry it wrote, or `null` when the caller named no id at all: a
 * server this set cannot identify is not marked, and the caller must say so
 * rather than believe a removal happened.
 */
export function rememberForgottenServer(
  input: ForgottenServerRef & { name?: string | null; at?: string },
  options: { storage?: ForgottenServerStorage | null } = {},
): ForgottenServer | null {
  hydrateForgottenServers(options.storage === undefined ? {} : { storage: options.storage });
  const cloudId = idText(input.cloudId);
  const hubId = idText(input.hubId);
  const key = cloudId ?? hubId;
  if (!key) return null;
  const entry: ForgottenServer = {
    key,
    cloudId,
    hubId,
    name: text(input.name),
    at: isoText(input.at) ?? new Date().toISOString(),
  };
  // The newest removal is first and replaces an older one for the same server:
  // the set answers one entry per key.
  forgottenServers.value = [entry, ...forgottenServers.value.filter((row) => row.key !== key)].slice(
    0,
    FORGOTTEN_SERVERS_LIMIT,
  );
  persist({ storage: options.storage });
  return entry;
}

/**
 * Take a server back: clear the removal that hid it, when any act on this device
 * re-establishes it — a typed address (`+ Dodaj strežnik`), opening it from
 * **Found on this network**, adopting it, its onboarding (plan §3/C4: the removal
 * must not survive as a permanent ghost).
 *
 * Cleared by **any** id the entry carries, because the way back knows different
 * ones than the removal did: `+ Dodaj strežnik` learns the hub's own id, while
 * the row the person removed may have been keyed by the account's row id.
 *
 * Answers the entry it cleared, or null when there was nothing to clear.
 */
export function restoreForgottenServer(
  ref: ForgottenServerRef,
  options: { storage?: ForgottenServerStorage | null } = {},
): ForgottenServer | null {
  hydrateForgottenServers(options.storage === undefined ? {} : { storage: options.storage });
  const ids = refIds(ref);
  if (!ids.length) return null;
  const mine = (entry: ForgottenServer): boolean =>
    [entry.key, entry.cloudId, entry.hubId].some((id) => !!id && ids.includes(id));
  const cleared = forgottenServers.value.find(mine) ?? null;
  if (!cleared) return null;
  forgottenServers.value = forgottenServers.value.filter((entry) => !mine(entry));
  persist({ storage: options.storage });
  return cleared;
}

// ─────────────────────────────────────────────────────────────────────────────
// The profile changed — this set is not that profile's any more
// ─────────────────────────────────────────────────────────────────────────────

// Dropped and re-read at once, for the same reason the buffers are
// (`services/serverProfiles.ts`): a removal taken under one account must not hide
// a row under another, and the table is a `computed` over this ref.
onProfileScopeChange(() => {
  hydrated = false;
  forgottenServers.value = [];
  hydrateForgottenServers({ force: true });
});
